/**
 * Single Post Display Component
 * Displays a single post with author info, content, likes, and comments.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthenticationContext';
import { capture } from '../../utils/analytics';
import { formatPostTimestampToRelative } from '../../utils/formatPostTimestampToRelative';
import { linkifyPostContent } from '../../utils/linkifyPostContent';
import { readingStats } from '../../utils/readingStats';
import { Repeat2, Pin, Trash2 } from 'lucide-react';
import PostLikeAndCommentButtons from './PostLikeAndCommentButtons';
import PostReactions from './PostReactions';
import PostCommentsListAndForm from './PostCommentsListAndForm';
import QuotedPostCard from './QuotedPostCard';
import PromptContextCard from './PromptContextCard';
import PostShareModal from './PostShareModal';
import VerifiedBadge from '../common/VerifiedBadge';
import './SinglePostDisplay.css';

const DELETE_WINDOW_MS = 60 * 1000; // authors can delete within 1 minute of posting

const SinglePostDisplay = ({ post, onUpdate, canPin, isPinned, onPinToggle }) => {
  const { user, token, setUser } = useAuth();
  // A repost with no commentary shows the original's writing; a quote repost shows
  // the reposter's own text plus the original embedded. Likes/comments act on this post.
  const isQuote = !!post.repostOf && !!(post.content || '').trim();
  const isRepost = !!post.repostOf && !isQuote;
  const src = isRepost ? post.repostOf : post;
  const [reposted, setReposted] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showRepostMenu, setShowRepostMenu] = useState(false);
  const [showQuoteBox, setShowQuoteBox] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [quoting, setQuoting] = useState(false);

  // Authors can delete their own post for 1 minute after posting.
  const isOwnPost = !!token && post.author?._id?.toString() === user?.id?.toString();
  const [deleted, setDeleted] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const canDelete = secondsLeft > 0;

  useEffect(() => {
    if (!isOwnPost) { setSecondsLeft(0); return undefined; }
    const remaining = () =>
      Math.max(0, Math.ceil((DELETE_WINDOW_MS - (Date.now() - new Date(post.createdAt).getTime())) / 1000));
    setSecondsLeft(remaining());
    const interval = setInterval(() => {
      const s = remaining();
      setSecondsLeft(s);
      if (s <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [isOwnPost, post.createdAt]);

  const handleDeletePost = async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await axios.delete(`/api/posts/${post._id}`);
      setDeleted(true);
      if (onUpdate) onUpdate();
    } catch (error) {
      window.alert(error.response?.data?.message || 'Could not delete post.');
    }
  };

  const handleQuoteSubmit = async () => {
    if (!quoteText.trim()) return;
    setQuoting(true);
    try {
      await axios.post(`/api/posts/${src._id}/repost`, { content: quoteText });
      setQuoteText('');
      setShowQuoteBox(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error quoting post:', error);
    } finally {
      setQuoting(false);
    }
  };

  const handleShare = () => setShowShare(true);

  const handleReport = async () => {
    if (!token) {
      window.location.href = '/login';
      return;
    }
    const reason = window.prompt('Why are you reporting this post? (optional)');
    if (reason === null) return; // cancelled
    try {
      const response = await axios.post(`/api/posts/${src._id}/report`, { reason });
      window.alert(response.data.message);
    } catch (error) {
      console.error('Error reporting post:', error);
    }
  };

  const handleRepost = async () => {
    if (!token) {
      window.location.href = '/login';
      return;
    }
    try {
      const response = await axios.post(`/api/posts/${src._id}/repost`);
      setReposted(response.data.reposted);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error reposting:', error);
    }
  };
  const [isBookmarked, setIsBookmarked] = useState(
    user?.bookmarks?.some(id => (id?._id || id)?.toString() === post._id?.toString()) || false
  );

  useEffect(() => {
    setIsBookmarked(
      user?.bookmarks?.some(id => (id?._id || id)?.toString() === post._id?.toString()) || false
    );
  }, [user?.bookmarks, post._id]);

  const handleBookmark = async () => {
    if (!token) {
      window.location.href = '/login';
      return;
    }
    try {
      const response = await axios.post(`/api/posts/${post._id}/bookmark`);
      setIsBookmarked(response.data.bookmarked);
      // Keep the auth context in sync so the Saved page and other cards reflect it.
      if (setUser) setUser(u => (u ? { ...u, bookmarks: response.data.bookmarks } : u));
    } catch (error) {
      console.error('Error bookmarking post:', error);
    }
  };
  const [isLiked, setIsLiked] = useState(
    post.likes?.some(like => {
      const likeId = like._id || like;
      return likeId?.toString() === user?.id?.toString();
    }) || false
  );
  const [likesCount, setLikesCount] = useState(post.likes?.length || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(post.comments || []);

  // Recalculate isLiked when user becomes available (e.g., after page refresh)
  useEffect(() => {
    if (user?.id) {
      setIsLiked(
        post.likes?.some(like => {
          const likeId = like._id || like;
          return likeId?.toString() === user?.id?.toString();
        }) || false
      );
    }
  }, [user?.id, post.likes]);

  const handleLike = async () => {
    if (!token) {
      window.location.href = '/login';
      return;
    }
    try {
      const response = await axios.post(`/api/posts/${post._id}/like`);
      setIsLiked(response.data.isLiked);
      setLikesCount(response.data.likes);
      if (response.data.isLiked) capture('like_given');
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleComment = async (content, parentComment = null) => {
    if (!token) {
      window.location.href = '/login';
      return;
    }

    try {
      const response = await axios.post(`/api/posts/${post._id}/comment`, {
        content,
        parentComment
      });
      setComments([...comments, response.data]);
      capture('comment_added');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleEditComment = async (commentId, content) => {
    try {
      const response = await axios.put(`/api/posts/${post._id}/comment/${commentId}`, { content });
      setComments(comments.map(c => (c._id === commentId ? response.data : c)));
    } catch (error) {
      console.error('Error editing comment:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`/api/posts/${post._id}/comment/${commentId}`);
      setComments(comments.filter(c => c._id !== commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!token) {
      window.location.href = '/login';
      return;
    }
    try {
      const response = await axios.post(`/api/posts/${post._id}/comment/${commentId}/like`);
      setComments(comments.map(c => {
        if (c._id !== commentId) return c;
        const likes = (c.likes || []).filter(id => (id._id || id)?.toString() !== user?.id?.toString());
        if (response.data.isLiked) likes.push(user.id);
        return { ...c, likes };
      }));
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };

  if (deleted) return null;

  return (
    <div className="post">
      {isPinned && (
        <div className="pinned-banner">
          <Pin size={14} fill="currentColor" />
          <span>Pinned post</span>
        </div>
      )}
      {isRepost && (
        <div className="repost-banner">
          <Repeat2 size={15} />
          <Link to={`/profile/${post.author?._id}`}>
            {post.author?.fullName || post.author?.username}
          </Link>
          <span>reposted</span>
        </div>
      )}
      <div className="post-header">
        <div className="post-author">
          <Link to={`/profile/${src.author?._id}`} className="post-author-link">
            {src.author?.avatar ? (
              <img src={src.author.avatar} alt={src.author.username} className="post-avatar" />
            ) : (
              <div className="post-avatar-placeholder">
                {src.author?.username?.charAt(0).toUpperCase()}
              </div>
            )}
          </Link>
          <div>
            <div className="post-author-name">
              <Link to={`/profile/${src.author?._id}`} className="post-author-link">
                {src.author?.fullName || src.author?.username}
              </Link>
              {src.author?.isVerified && <VerifiedBadge />}
            </div>
            <div className="post-meta">
              <Link to={`/post/${src._id}`} className="post-date">
                {formatPostTimestampToRelative(src.createdAt)}
              </Link>
              {(() => {
                const { words, minutes } = readingStats(src.content);
                return words > 0 ? (
                  <span className="reading-time">· {words} words · {minutes} min read</span>
                ) : null;
              })()}
            </div>
          </div>
        </div>
        <div className="post-header-right">
          {src.isContestWinner && (
            <span
              title="Weekly contest winner"
              style={{ fontSize: 12, fontWeight: 600, color: '#92610a', background: '#fdf3d8', border: '1px solid #ecd693', borderRadius: 12, padding: '2px 8px' }}
            >
              🏆 Contest winner
            </span>
          )}
          {src.isFeatured && (
            <span
              title="Featured by an editor"
              style={{ fontSize: 12, fontWeight: 600, color: '#b8860b', background: '#fff7e0', border: '1px solid #f0d98c', borderRadius: 12, padding: '2px 8px' }}
            >
              ⭐ Featured
            </span>
          )}
          {src.wordCategory && (
            <span className={`word-category-badge category-${src.wordCategory}`}>
              {src.wordCategory} words
            </span>
          )}
          {canPin && (
            <button
              type="button"
              className={`pin-btn ${isPinned ? 'pinned' : ''}`}
              onClick={() => onPinToggle(post._id)}
              title={isPinned ? 'Unpin from profile' : 'Pin to profile'}
            >
              <Pin size={16} fill={isPinned ? 'currentColor' : 'none'} />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="delete-btn"
              onClick={handleDeletePost}
              title="You can delete this post for a limited time after posting"
            >
              <Trash2 size={16} />
              <span className="delete-countdown">{secondsLeft}s</span>
            </button>
          )}
        </div>
      </div>

      {src.prompt && <PromptContextCard prompt={src.prompt} />}

      <div className="post-content">
        <p>{linkifyPostContent(src.content)}</p>
      </div>

      {isQuote && <QuotedPostCard post={post.repostOf} />}

      <PostReactions
        postId={post._id}
        reactions={post.reactions}
        currentUserId={user?.id}
        isAuthenticated={!!token}
      />

      <PostLikeAndCommentButtons
        isLiked={isLiked}
        likesCount={likesCount}
        commentsCount={comments.length}
        onLikeClick={handleLike}
        onCommentToggle={() => setShowComments(!showComments)}
        isAuthenticated={!!token}
        isBookmarked={isBookmarked}
        onBookmarkClick={handleBookmark}
        isReposted={reposted}
        onRepostClick={() => {
          if (!token) { window.location.href = '/login'; return; }
          setShowRepostMenu((v) => !v);
        }}
        onShareClick={handleShare}
        onReportClick={
          token && src.author?._id?.toString() !== user?.id?.toString() ? handleReport : undefined
        }
      />

      {showRepostMenu && (
        <div className="repost-menu">
          <button
            type="button"
            onClick={() => { handleRepost(); setShowRepostMenu(false); }}
          >
            {reposted ? 'Undo repost' : 'Repost'}
          </button>
          <button
            type="button"
            onClick={() => { setShowQuoteBox(true); setShowRepostMenu(false); }}
          >
            Quote
          </button>
        </div>
      )}

      {showQuoteBox && (
        <div className="quote-box">
          <textarea
            placeholder="Add a comment…"
            value={quoteText}
            onChange={(e) => setQuoteText(e.target.value)}
            rows={3}
            autoFocus
          />
          <div className="quote-box-actions">
            <button type="button" onClick={handleQuoteSubmit} disabled={quoting || !quoteText.trim()}>
              {quoting ? 'Posting…' : 'Post'}
            </button>
            <button type="button" onClick={() => { setShowQuoteBox(false); setQuoteText(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showShare && (
        <PostShareModal post={src} onClose={() => setShowShare(false)} />
      )}

      {showComments && (
        <PostCommentsListAndForm
          comments={comments}
          isAuthenticated={!!token}
          onSubmitComment={handleComment}
          currentUserId={user?.id}
          postAuthorId={post.author?._id}
          onEditComment={handleEditComment}
          onDeleteComment={handleDeleteComment}
          onLikeComment={handleLikeComment}
        />
      )}
    </div>
  );
};

export default SinglePostDisplay;
